/**
 * The Stock List order form at `/stock-list/purchase`: it collects a US shipping address, email,
 * phone and quantity, shows the total, and sends the buyer to PayPal to pay. It starts the only
 * purchase this repository takes money for: the printed Ryerson Stock List.
 *
 * Flow:
 * 1. `components/StockList/Hero.tsx` (on `/stock-list`) links here with `?qty=<n>`. A positive
 *    `qty` replaces the default quantity of 1.
 * 2. `onFormSubmit` posts `{ quantity, shipmentInfo }` to `APIUrl.STOCK_LIST_ORDER`, served by
 *    `web-payments-api/application/src/functions/PostPaypalOrderStocklist/`. The Payments API
 *    creates the PayPal payment and answers with PayPal's approval `redirectUrl`, and the browser
 *    goes there. No money has moved yet.
 * 3. PayPal sends the buyer back to `/stock-list/complete?paymentId=…&PayerID=…`, where
 *    `pages/stock-list/complete/index.tsx` asks the Payments API to execute the payment, or, on
 *    cancel, back to `/stock-list/purchase`. The Payments API builds both URLs on the origin of
 *    this request's `Referer` (`web-payments-api/application/src/enums/paypal.enum.ts` →
 *    `EPaypalRedirectUrl`), so both pages must stay at those paths.
 *
 * Rules a change must keep:
 * - The Stock List is sold only on the Ryerson build, only to US addresses that aren't PO boxes,
 *   at the price the Payments API charges.
 *   - Ryerson only: `pages/stock-list/purchase/index.tsx` returns `notFound` on any other build.
 *   - US only: the state list is `data/states.ts` → `"United States"`, the zip must match
 *     `PostalCodeRegexPatterns.us`, and `country` is always sent as `"US"`.
 *   - No PO boxes: nothing in this form or in the Payments API rejects one. Only the page copy
 *     (`pages/stock-list/purchase/index.tsx` → `PurchaseContentHero` → `shipping`) tells the buyer.
 *   - The price: the Payments API charges `EPaypalStocklist.Price` × quantity
 *     (`web-payments-api/application/src/enums/paypal.enum.ts`) and never reads an amount from this
 *     request. `pricePerEach` here only sets the total the buyer is shown, so it must equal
 *     `EPaypalStocklist.Price`. The same price is also the literal `HeroContent.price` in
 *     `content/ryerson/stock-list.ts` (the landing page). Nothing ties the three together, so a
 *     price change edits all three in the same release. The displayed total is computed here from
 *     a local literal, unlike web-architecture ADR-0027 §1 and ADR-0028 (the UI shows only money
 *     an API returned); no clean-up is planned for this repo.
 * - The request body's field names are the ones the Payments API maps
 *   (`web-payments-api/application/src/mappers/paypal.mapper.ts` →
 *   `mapPaypalOrderStockListRequestBodyToModel`).
 *
 * Failure behaviour: a 2xx response other than 200 shows `content.modalMessageError` for eight
 * seconds. A rejected request, which with axios is any 4xx, 5xx or network error, ends in
 * `.catch(() => {})`: the buyer sees no message and stays on the form.
 *
 * Related: `pages/stock-list/purchase/index.tsx` (the page and its copy),
 * `pages/stock-list/complete/index.tsx` (the second half of the purchase),
 * `enums/apiUrl.enums.ts` → `APIUrl.STOCK_LIST_ORDER` (*Legacy: don't copy*).
 */
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { useApplication } from "@ryerson/frontend.application";
import {
	Input,
	EmailInput,
	PhoneInput,
	Option,
	Dropdown,
	IncrementDecrementInput,
	ZipCodeInput,
} from "@ryerson/frontend.form-controls";
import { Icon } from "@ryerson/frontend.assets";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Media } from "@ryerson/frontend.next";
import { Typography } from "@ryerson/frontend.typography";
import React, { useState } from "react";
import { Breadcrumb } from "@ryerson/frontend.navigation";
import { Button } from "@ryerson/frontend.button";
import styled from "@emotion/styled";
import { APIUrl } from "@enums/apiUrl.enums";
import {
	sendGAEvent,
	GoogleAnalyticsEventName,
	GoogleAnalyticsEventActionNames,
	PostalCodeRegexPatterns,
	validateZipCode,
} from "@ryerson/frontend.common";
import { states } from "../../data/states";
import Spacer from "@components/Shared/Spacer/Spacer";

/**
 * The form's copy, one object per language: `pages/stock-list/purchase/index.tsx` →
 * `PurchaseContentHero`. `modalMessageSuccess` is defined there but nothing renders it, because a
 * successful order leaves the page for PayPal.
 */
export type FormContent = {
	requiredDirections: string;
	title: string;
	shipping: string;
	firstNameLabel: string;
	lastNameLabel: string;
	address1Label: string;
	address2Label: string;
	cityLabel: string;
	stateLabel: string;
	zipLabel: string;
	emailLabel: string;
	phoneLabel: string;
	quantityLabel: string;
	totalLabel: string;
	modalMessageSuccess: string;
	modalMessageError: string;
	buttonLabel: string;
	priceLabel: string;
	orderSummaryLabel: string;
};

type StocklistFormProps = {
	content: FormContent;
};

const Messager = styled.div`
	display: block;
	width: 100%;
	height: auto;
	margin-top: 20px;
`;

const MessagerIcon = styled.div`
	display: inline-block;
	width: 30px;
	height: 30px;
	margin-right: 10px;
	vertical-align: middle;
	box-sizing: border-box;
	border-radius: 30px;
	text-align: center;
	line-height: 38px;
`;

const MessagerText = styled.div`
	display: inline-block;
	vertical-align: middle;
	width: calc(100% - 40px);
	box-sizing: border-box;
`;

const BreadcrumbContainer = styled.div`
	margin-top: 32px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-top: 8px;
			}
		`;
	}}
`;

const TotalsContainer = styled.div`
	display: block;
	width: 100%;
	height: 40px;
	border-radius: 2px;
	padding: 10px 20px;
	box-sizing: border-box;
	margin-top: 5px;
`;

const Left = styled.div`
	display: inline-block;
	vertical-align: top;
	width: calc(100% - 400px);
	height: auto;
`;

const FormContainer = styled.div`
	display: block;
	width: 100%;
	max-width: 460px;
	height: auto;
`;

const Right = styled.div`
	display: inline-block;
	width: 390px;
	margin-left: 10px;
	vertical-align: top;
`;

const CoverImage = styled.div`
	display: block;
	width: 100%;
	height: 330px;
	margin-bottom: 10px;
	background-size: cover;
	background-position: center center;
	background-repeat: no-repeat;
	background-image: url(/images/stock-list/StockbookThumbnail.svg);
`;

const OrderSummary = styled.div`
	display: block;
	width: 100%;
	height: auto;
`;

const OrderDetails = styled.div`
	display: inline-block;
	width: calc(100% - 95px);
	height: auto;
	vertical-align: top;
`;

const Divider = styled.hr`
	display: block;
	width: 100%;
	margin-top: 10px;
	margin-bottom: 25px;
	opacity: 0.1;
`;

/**
 * The Stock List order form, with a desktop layout (`lg` and up: form left, cover image and order
 * summary right) and a single-column mobile layout. Both share one set of state. See the file
 * header for the flow and the rules.
 */
const StocklistForm: React.FC<StocklistFormProps> = ({ content }) => {
	const {
		localization: { language },
		axiosInstance,
	} = useApplication();
	const { theme } = useTheme();

	const inputStyling = css`
		box-sizing: border-box;
		margin-bottom: 20px;
		@media only screen and (max-width: ${theme.breakpoints.lg}) {
			margin-bottom: 15px;
		}
	`;

	// Display only: must equal `web-payments-api` `EPaypalStocklist.Price`, which is what PayPal
	// charges per copy. See the file header.
	const pricePerEach = 9.95;

	const [submitted, setSubmitted] = useState<boolean>(false);
	const [firstName, setFirstName] = React.useState<string>("");
	const [lastName, setLastName] = React.useState<string>("");
	const [address1, setAddress1] = React.useState<string>("");
	const [address2, setAddress2] = React.useState<string>("");
	const [city, setCity] = React.useState<string>("");
	const [state, setState] = React.useState<string>("");
	const [zip, setZip] = React.useState<string>("");
	const [email, setEmail] = React.useState<string>("");
	const [phoneNumber, setPhoneNumber] = React.useState<string>("");
	const [quantity, setQuantity] = React.useState<string>("1");
	const [total, setTotal] = React.useState<number>(0.0);

	const [showAPIMessage, setShowAPIMessage] = React.useState<boolean>(false);
	const [apiMessage, setAPIMessage] = React.useState<string>("");
	const [apiStatus, setAPIStatus] = React.useState<string>("");

	const isZipCodeValid = (): boolean => {
		return validateZipCode(zip, PostalCodeRegexPatterns.us, language) === "";
	};

	// The displayed total. It isn't sent: the Payments API recomputes the amount from `quantity`.
	React.useEffect(() => {
		if (!isNaN(Number(quantity)) && Number(quantity) > 0) {
			setTotal(pricePerEach * Number(quantity));
		} else {
			setTotal(0);
		}
	}, [quantity]);

	// Takes the quantity picked on the landing page (`Hero.tsx` → `?qty=<n>`). Any positive number
	// is accepted, including a fraction.
	React.useEffect(() => {
		if (window) {
			const queryString = window.location.search;
			if (queryString) {
				const urlParams = new URLSearchParams(queryString);
				if (urlParams.get("qty")) {
					let qty = urlParams.get("qty");
					if (Number(qty) > 0) {
						setQuantity(String(qty));
					}
				}
			}
		}
	}, []);

	/**
	 * Validates the required fields and, when they pass, creates the PayPal order and sends the
	 * browser to PayPal's approval page. Address 2 is the only optional field. The button isn't
	 * disabled while the request is in flight, so each click posts a new order.
	 */
	const onFormSubmit = () => {
		setShowAPIMessage(false);
		setSubmitted(true);
		if (
			firstName.length > 0 &&
			lastName.length > 0 &&
			email.length > 0 &&
			address1.length > 0 &&
			isZipCodeValid() &&
			city.length > 0 &&
			state.length > 0 &&
			phoneNumber.length > 0 &&
			Number(quantity) > 0
		) {
			let saveObject: object = {
				quantity: Number(quantity),
				shipmentInfo: {
					firstName: firstName,
					lastName: lastName,
					email: email,
					telephone: String(phoneNumber),
					address1: address1,
					address2: address2,
					city: city,
					state: state,
					zip: String(zip),
					country: "US",
				},
			};
			// Fires on submit, before the order exists, so it counts attempts rather than paid
			// orders.
			sendGAEvent(GoogleAnalyticsEventName.STOCK_LIST, {
				action: GoogleAnalyticsEventActionNames.PURCHASE,
				value: quantity,
			});
			axiosInstance
				.post(APIUrl.STOCK_LIST_ORDER, saveObject)
				.then((response) => {
					if (response.status === 200) {
						let res = response.data;
						// PayPal's approval URL (`PaypalOrderResponse.redirectUrl` in
						// `web-payments-api`).
						window.location.href = res.redirectUrl;
					} else {
						setAPIMessage(content.modalMessageError ?? "");
						setSubmitted(false);
						setShowAPIMessage(true);
						setAPIStatus("error");
						setTimeout(() => {
							setShowAPIMessage(false);
						}, 8000);
					}
				})
				// Axios rejects every 4xx, 5xx and network error, so those land here and the
				// buyer is shown nothing.
				.catch(() => {});
		} else {
			setAPIStatus("error");
			setAPIMessage("Please enter all required fields.");
			setShowAPIMessage(true);
			setTimeout(() => {
				setShowAPIMessage(false);
			}, 8000);
		}
	};

	let stateOptions: Option[] = [];
	for (let i = 0; i < states["United States"].length; i++) {
		stateOptions.push({
			value: states["United States"][i].id,
			display: states["United States"][i].label,
		});
	}

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection type="tertiary">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Typography
						variant="h1"
						type="tertiary"
						css={css`
							margin-top: 16px;
							margin-bottom: 25px;
							padding-bottom: 5px;
							@media (max-width: ${theme.breakpoints.lg}) {
								margin-bottom: 22px;
							}
						`}
					>
						{content.title}
					</Typography>
					<Typography
						variant="p"
						type="tertiary"
						css={css`
							margin-bottom: 15px;
							padding-bottom: 13px;
							@media (max-width: ${theme.breakpoints.lg}) {
								margin-bottom: 12px;
							}
						`}
					>
						{content.shipping}
					</Typography>
					<Left>
						<ContentSection type="secondary" hPadding="42px">
							<FormContainer>
								<Typography
									variant="p"
									size="xs"
									color={theme.colors.primary.gray}
									italic={true}
								>
									{content.requiredDirections}
								</Typography>
								<Input
									label={content.firstNameLabel}
									value={firstName}
									onChange={(e) => {
										setFirstName(e.target.value);
									}}
									required={true}
									submitted={submitted}
									css={inputStyling}
									language={language}
								/>
								<Input
									label={content.lastNameLabel}
									value={lastName}
									onChange={(e) => {
										setLastName(e.target.value);
									}}
									required={true}
									submitted={submitted}
									css={inputStyling}
									language={language}
								/>
								<Input
									label={content.address1Label}
									value={address1}
									onChange={(e) => {
										setAddress1(e.target.value);
									}}
									required={true}
									submitted={submitted}
									css={inputStyling}
									language={language}
								/>
								<Input
									label={content.address2Label}
									value={address2}
									onChange={(e) => {
										setAddress2(e.target.value);
									}}
									required={false}
									submitted={submitted}
									css={inputStyling}
									language={language}
								/>
								<Flex direction="row" justifyContent="space-between">
									<FlexItem
										css={css`
											width: calc(45% - 10px);
										`}
									>
										<Input
											label={content.cityLabel}
											value={city}
											onChange={(e) => {
												setCity(e.target.value);
											}}
											required={true}
											submitted={submitted}
											css={inputStyling}
											language={language}
										/>
									</FlexItem>
									<FlexItem
										css={css`
											width: 10px;
										`}
									/>
									<FlexItem
										css={css`
											width: calc(35% - 10px);
										`}
									>
										<Dropdown
											language={language}
											options={stateOptions}
											label={content.stateLabel}
											value={state}
											submitted={submitted}
											required={true}
											onChange={(val) => {
												setState(val);
											}}
										/>
									</FlexItem>
									<FlexItem
										css={css`
											width: 10px;
										`}
									/>
									<FlexItem
										css={css`
											width: 20%;
										`}
									>
										<ZipCodeInput
											label={content.zipLabel}
											value={zip}
											onChange={(e) => {
												setZip(e.target.value);
											}}
											required={true}
											submitted={submitted}
											css={inputStyling}
											pattern={PostalCodeRegexPatterns.us}
											language={language}
											isValid={!submitted}
										/>
									</FlexItem>
								</Flex>

								<EmailInput
									label={content.emailLabel}
									value={email}
									onChange={(e) => {
										setEmail(e.target.value);
									}}
									required={true}
									submitted={submitted}
									css={inputStyling}
									language={language}
								/>
								<PhoneInput
									label={content.phoneLabel}
									value={phoneNumber}
									onChange={(e) => {
										setPhoneNumber(e.target.value);
									}}
									required={true}
									submitted={submitted}
									css={inputStyling}
									language={language}
								/>
								<Flex direction="row" justifyContent="space-between">
									<FlexItem
										css={css`
											width: calc(35% - 5px);
										`}
									>
										<IncrementDecrementInput
											label={content.quantityLabel}
											required={true}
											onIncrement={() => {
												setQuantity(String(Number(quantity) + 1));
											}}
											onDecrement={() => {
												if (Number(quantity) > 1) {
													setQuantity(String(Number(quantity) - 1));
												}
											}}
											onChange={(e) => {
												setQuantity(e.target.value);
											}}
											value={quantity}
											language={language}
										/>
									</FlexItem>
									<FlexItem
										css={css`
											width: 10px;
										`}
									/>
									<FlexItem
										css={css`
											width: calc(65% - 5px);
										`}
									>
										<Typography
											variant="div"
											size="md"
											color={theme.colors.primary.header}
										>
											{content.totalLabel}
										</Typography>
										<TotalsContainer
											css={css`
												border: 1px solid ${theme.colors.primary.lightGray};
											`}
										>
											${total.toFixed(2)}
										</TotalsContainer>
									</FlexItem>
								</Flex>
								<Spacer />
								<Button
									size="lg"
									type="primary"
									label={content.buttonLabel}
									onClick={onFormSubmit}
								/>
								{showAPIMessage && (
									<Messager>
										<MessagerIcon
											css={css`
												background-color: ${apiStatus === "success"
													? theme.colors.secondary.green
													: theme.colors.secondary.red};
											`}
										>
											<Icon
												size="sm"
												color={theme.colors.primary.white}
												icon={apiStatus === "success" ? "check" : "minus"}
											/>
										</MessagerIcon>
										<MessagerText>
											<Typography variant="div" size="md" type="primary">
												{apiMessage}
											</Typography>
										</MessagerText>
									</Messager>
								)}
							</FormContainer>
						</ContentSection>
					</Left>
					<Right>
						<CoverImage />
						<OrderSummary>
							<ContentSection vPadding="20px" hPadding="20px" type="primary">
								<Typography variant="h3">{content.orderSummaryLabel}</Typography>
								<Divider
									css={css`
										color: ${theme.colors.primary.darkerGray};
									`}
								/>
								<OrderDetails>
									<Typography
										variant="div"
										size="md"
										color={theme.colors.primary.header}
									>
										{content.title}
									</Typography>
									<Flex
										direction="row"
										justifyContent="space-between"
										css={css`
											margin-top: 15px;
										`}
									>
										<FlexItem
											css={css`
												width: 50%;
											`}
										>
											<Typography variant="div" size="sm" type="primary">
												{content.quantityLabel}
											</Typography>
										</FlexItem>
										<FlexItem
											css={css`
												width: 50%;
											`}
										>
											<Typography
												variant="div"
												size="sm"
												weight="bold"
												color={theme.colors.primary.header}
												type="primary"
											>
												{quantity} pc(s)
											</Typography>
										</FlexItem>
									</Flex>
									<Flex
										direction="row"
										justifyContent="space-between"
										css={css`
											margin-top: 10px;
										`}
									>
										<FlexItem
											css={css`
												width: 50%;
											`}
										>
											<Typography variant="div" size="sm" type="primary">
												{content.priceLabel}
											</Typography>
										</FlexItem>
										<FlexItem
											css={css`
												width: 50%;
											`}
										>
											<Typography
												variant="div"
												size="sm"
												weight="bold"
												color={theme.colors.primary.header}
												type="primary"
											>
												USD ${total.toFixed(2)}
											</Typography>
										</FlexItem>
									</Flex>
								</OrderDetails>
								<Divider
									css={css`
										color: ${theme.colors.primary.darkerGray};
									`}
								/>
								<Flex direction="row" justifyContent="space-between">
									<FlexItem>
										<Typography
											variant="div"
											size="lg"
											color={theme.colors.primary.header}
											type="primary"
										>
											{content.totalLabel}
										</Typography>
									</FlexItem>
									<FlexItem>
										<Flex
											direction="row"
											justifyContent="space-between"
											alignItems="baseline"
										>
											<FlexItem>
												<Typography
													variant="div"
													size="lg"
													color={theme.colors.primary.header}
													type="primary"
												>
													USD
												</Typography>
											</FlexItem>
											<FlexItem>
												<Typography variant="h3" type="primary">
													${total.toFixed(2).split(".").shift()}
												</Typography>
											</FlexItem>
											<FlexItem>
												<Typography
													variant="div"
													size="lg"
													color={theme.colors.primary.header}
													type="primary"
												>
													.{total.toFixed(2).split(".").pop()}
												</Typography>
											</FlexItem>
										</Flex>
									</FlexItem>
								</Flex>
							</ContentSection>
						</OrderSummary>
					</Right>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="tertiary">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Typography
						variant="h1"
						type="tertiary"
						css={css`
							margin-top: 16px;
							margin-bottom: 50px;
							padding-bottom: 13px;
							@media (max-width: ${theme.breakpoints.lg}) {
								margin-bottom: 22px;
							}
						`}
					>
						{content.title}
					</Typography>
					<Typography
						variant="p"
						type="tertiary"
						css={css`
							margin-bottom: 15px;
							padding-bottom: 13px;
							@media (max-width: ${theme.breakpoints.lg}) {
								margin-bottom: 12px;
							}
						`}
					>
						{content.shipping}
					</Typography>
				</ContentSection>
				<ContentSection>
					<Typography
						variant="p"
						size="xs"
						color={theme.colors.primary.gray}
						italic={true}
					>
						{content.requiredDirections}
					</Typography>
					<Input
						label={content.firstNameLabel}
						value={firstName}
						onChange={(e) => {
							setFirstName(e.target.value);
						}}
						required={true}
						submitted={submitted}
						css={inputStyling}
						language={language}
					/>
					<Input
						label={content.lastNameLabel}
						value={lastName}
						onChange={(e) => {
							setLastName(e.target.value);
						}}
						required={true}
						submitted={submitted}
						css={inputStyling}
						language={language}
					/>
					<Input
						label={content.address1Label}
						value={address1}
						onChange={(e) => {
							setAddress1(e.target.value);
						}}
						required={true}
						submitted={submitted}
						css={inputStyling}
						language={language}
					/>
					<Input
						label={content.address2Label}
						value={address2}
						onChange={(e) => {
							setAddress2(e.target.value);
						}}
						required={false}
						submitted={submitted}
						css={inputStyling}
						language={language}
					/>

					<Input
						label={content.cityLabel}
						value={city}
						onChange={(e) => {
							setCity(e.target.value);
						}}
						required={true}
						submitted={submitted}
						css={inputStyling}
						language={language}
					/>

					<Dropdown
						language={language}
						options={stateOptions}
						label={content.stateLabel}
						value={state}
						submitted={submitted}
						required={true}
						onChange={(val) => {
							setState(val);
						}}
					/>
					<Spacer
						css={css`
							height: 15px;
						`}
					/>
					<ZipCodeInput
						label={content.zipLabel}
						value={zip}
						onChange={(e) => {
							setZip(e.target.value);
						}}
						required={true}
						submitted={submitted}
						css={inputStyling}
						pattern={PostalCodeRegexPatterns.us}
						language={language}
						isValid={!submitted}
					/>

					<EmailInput
						label={content.emailLabel}
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
						}}
						required={true}
						submitted={submitted}
						css={inputStyling}
						language={language}
					/>
					<PhoneInput
						label={content.phoneLabel}
						value={phoneNumber}
						onChange={(e) => {
							setPhoneNumber(e.target.value);
						}}
						required={true}
						submitted={submitted}
						css={inputStyling}
						language={language}
					/>
					<Flex direction="row" justifyContent="space-between">
						<FlexItem
							css={css`
								width: calc(50% - 5px);
							`}
						>
							<IncrementDecrementInput
								label={content.quantityLabel}
								required={true}
								onIncrement={() => {
									setQuantity(String(Number(quantity) + 1));
								}}
								onDecrement={() => {
									if (Number(quantity) > 1) {
										setQuantity(String(Number(quantity) - 1));
									}
								}}
								onChange={(e) => {
									setQuantity(e.target.value);
								}}
								value={quantity}
								language={language}
							/>
						</FlexItem>
						<FlexItem
							css={css`
								width: 10px;
							`}
						/>
						<FlexItem
							css={css`
								width: calc(50% - 5px);
							`}
						>
							<Typography variant="div" size="md" color={theme.colors.primary.header}>
								{content.totalLabel}
							</Typography>
							<TotalsContainer
								css={css`
									border: 1px solid ${theme.colors.primary.lightGray};
									height: 50px;
									padding: 15px 20px;
								`}
							>
								${total.toFixed(2)}
							</TotalsContainer>
						</FlexItem>
					</Flex>
					<Spacer
						css={css`
							height: 25px;
						`}
					/>
					<Typography variant="h3">{content.orderSummaryLabel}</Typography>
					<Divider
						css={css`
							color: ${theme.colors.primary.darkerGray};
						`}
					/>
					<OrderDetails>
						<Typography variant="div" size="md" color={theme.colors.primary.header}>
							{content.title}
						</Typography>
						<Flex
							direction="row"
							justifyContent="space-between"
							css={css`
								margin-top: 15px;
							`}
						>
							<FlexItem
								css={css`
									width: 50%;
								`}
							>
								<Typography variant="div" size="sm" type="primary">
									{content.quantityLabel}
								</Typography>
							</FlexItem>
							<FlexItem
								css={css`
									width: 50%;
								`}
							>
								<Typography
									variant="div"
									size="sm"
									weight="bold"
									color={theme.colors.primary.header}
									type="primary"
								>
									{quantity} pc(s)
								</Typography>
							</FlexItem>
						</Flex>
						<Flex
							direction="row"
							justifyContent="space-between"
							css={css`
								margin-top: 10px;
							`}
						>
							<FlexItem
								css={css`
									width: 50%;
								`}
							>
								<Typography variant="div" size="sm" type="primary">
									{content.priceLabel}
								</Typography>
							</FlexItem>
							<FlexItem
								css={css`
									width: 50%;
								`}
							>
								<Typography
									variant="div"
									size="sm"
									weight="bold"
									color={theme.colors.primary.header}
									type="primary"
								>
									USD ${total.toFixed(2)}
								</Typography>
							</FlexItem>
						</Flex>
					</OrderDetails>
					<Divider
						css={css`
							color: ${theme.colors.primary.darkerGray};
						`}
					/>
					<Flex direction="row" justifyContent="space-between">
						<FlexItem>
							<Typography
								variant="div"
								size="lg"
								color={theme.colors.primary.header}
								type="primary"
							>
								{content.totalLabel}
							</Typography>
						</FlexItem>
						<FlexItem>
							<Flex
								direction="row"
								justifyContent="space-between"
								alignItems="baseline"
							>
								<FlexItem>
									<Typography
										variant="div"
										size="lg"
										color={theme.colors.primary.header}
										type="primary"
									>
										USD
									</Typography>
								</FlexItem>
								<FlexItem>
									<Typography variant="h3" type="primary">
										${total.toFixed(2).split(".").shift()}
									</Typography>
								</FlexItem>
								<FlexItem>
									<Typography
										variant="div"
										size="lg"
										color={theme.colors.primary.header}
										type="primary"
									>
										.{total.toFixed(2).split(".").pop()}
									</Typography>
								</FlexItem>
							</Flex>
						</FlexItem>
					</Flex>
					<Spacer
						css={css`
							height: 25px;
						`}
					/>
					<Button
						size="md"
						fullwidth={true}
						type="primary"
						label={content.buttonLabel}
						onClick={onFormSubmit}
					/>
					{showAPIMessage && (
						<Messager>
							<MessagerIcon
								css={css`
									background-color: ${apiStatus === "success"
										? theme.colors.secondary.green
										: theme.colors.secondary.red};
								`}
							>
								<Icon
									size="sm"
									color={theme.colors.primary.white}
									icon={apiStatus === "success" ? "check" : "minus"}
								/>
							</MessagerIcon>
							<MessagerText>
								<Typography variant="div" size="md" type="primary">
									{apiMessage}
								</Typography>
							</MessagerText>
						</Messager>
					)}
				</ContentSection>
				<Spacer
					css={css`
						height: 25px;
					`}
				/>
			</Media>
		</>
	);
};

export default StocklistForm;
